import React from 'react';
import { Cable } from 'lucide-react';
import type { PluginDefinition } from '@/plugins/types';
import { APP_VERSION } from '@/app/version';

// ─── Lazy-loaded Pages ─────────────────────────────────────────────────────────

const ConnectorDashboardPage = React.lazy(
  () => import('./pages/ConnectorDashboardPage'),
);

const ConnectorDetailPage = React.lazy(
  () => import('./pages/ConnectorDetailPage'),
);

const ConnectorCreateWizard = React.lazy(
  () => import('./pages/ConnectorCreateWizard'),
);

// ─── Suspense Fallback ─────────────────────────────────────────────────────────

const fallback = (
  <div className="flex h-full items-center justify-center text-muted-foreground text-sm">
    Lädt…
  </div>
);

// ─── Plugin Definition ─────────────────────────────────────────────────────────

export const connectorHubPlugin: PluginDefinition = {
  id: 'connector-hub',
  name: 'Sopho Connector',
  version: APP_VERSION,
  description: 'Externe Datenquellen verbinden, synchronisieren und überwachen.',
  internal: true,
  locked: true,
  group: 'data',
  // DevPlan 0139/T3: Connector-Verwaltung eines Workspace → WS-Admin statt Org-Admin.
  // MERGE-ORDER: Das Backend-Pendant (F2, SourceConnectorController → WS-Admin via
  // WorkspaceAuthService) liegt im separaten Hotfix PR #2728 und ist hier noch NICHT
  // gemergt. Diese FE-Änderung sollte erst MIT/NACH #2728 live gehen — sonst sieht ein
  // reiner WS-Admin (kein Org-Admin) connector-hub, läuft aber backend-seitig ins 403.
  accessControl: { minWorkspaceRole: 'Admin' },
  initializationOrder: 40,
  initialize: () => undefined,
  navigation: [
    {
      id: 'connector-hub',
      label: 'Sopho Connector',
      path: '/app/connectors',
      icon: Cable,
      order: 40,
      placement: 'footer' as const,
      adminOnly: true,
    },
  ],
  routes: [
    {
      // Mehrere Routes teilen sich dieselbe id (= Plugin-Id) für den RoleGate-Lookup;
      // React-Key wird in renderPluginRoutes aus dem Pfad gebildet.
      id: 'connector-hub',
      path: 'connectors',
      element: <React.Suspense fallback={fallback}><ConnectorDashboardPage /></React.Suspense>,
    },
    {
      id: 'connector-hub',
      path: 'connectors/new',
      element: <React.Suspense fallback={fallback}><ConnectorCreateWizard /></React.Suspense>,
    },
    {
      id: 'connector-hub',
      path: 'connectors/:id',
      element: <React.Suspense fallback={fallback}><ConnectorDetailPage /></React.Suspense>,
    },
  ],
};
